import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Order, OrderStatus } from '../types';
import { formatEuro } from '../utils/format';
import { playServedCelebrationSound } from '../utils/audio';
import confetti from 'canvas-confetti';
import {
  CheckCircle2,
  Clock,
  Flame,
  Utensils,
  Bell,
  AlertTriangle,
  RotateCcw,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Send,
  ChefHat
} from 'lucide-react';

export const LiveOrderTracker: React.FC = () => {
  const {
    activeCustomerOrder,
    cancelCustomerOrder,
    requestOrderChange,
    setActiveCustomerOrder,
    soundEnabled
  } = useApp();

  const { lang, t } = useLanguage();

  const [isItemsExpanded, setIsItemsExpanded] = useState(false);
  const prevStatusRef = useRef<OrderStatus | null>(null);

  if (!activeCustomerOrder) return null;

  const order = activeCustomerOrder;

  // Trigger celebration sound & confetti when order becomes ready or completed
  useEffect(() => {
    if (prevStatusRef.current && prevStatusRef.current !== order.status) {
      if (order.status === 'completed' || order.status === 'ready') {
        if (soundEnabled) {
          playServedCelebrationSound();
        }
        confetti({
          particleCount: 90,
          spread: 80,
          origin: { y: 0.6 }
        });
      }
    }
    prevStatusRef.current = order.status;
  }, [order.status, soundEnabled]);

  const statusStages: {
    key: OrderStatus;
    labelEn: string;
    labelFi: string;
    subEn: string;
    subFi: string;
    icon: any;
    color: string;
    animClass?: string;
  }[] = [
    {
      key: 'submitted',
      labelEn: 'Order Received',
      labelFi: 'Tilaus vastaanotettu',
      subEn: 'Transmitting directly to kitchen queue...',
      subFi: 'Siirtyy keittiön työjonoon...',
      icon: Clock,
      color: 'var(--accent-amber)',
      animClass: 'anim-radar-pulse'
    },
    {
      key: 'confirmed',
      labelEn: 'Chef Confirmed',
      labelFi: 'Keittiö vahvisti tilauksen',
      subEn: 'Ingredients are being prepped fresh',
      subFi: 'Tuoreet raaka-aineet valmistelussa',
      icon: ChefHat,
      color: '#38bdf8'
    },
    {
      key: 'preparing',
      labelEn: 'Baking in 450°C Wood-Fired Oven',
      labelFi: 'Paistumassa 450°C Puu-uunissa',
      subEn: 'Artisan sourdough pizzas baking in stone hearth...',
      subFi: 'Artesaanipizzat paistumassa kiviuunissa...',
      icon: Flame,
      color: 'var(--accent-copper)',
      animClass: 'anim-fire-flicker'
    },
    {
      key: 'ready',
      labelEn: 'Ready to Serve',
      labelFi: 'Valmis tarjoiltavaksi',
      subEn: 'Dishes are on their way to your table',
      subFi: 'Annokset ovat matkalla pöytääsi',
      icon: Bell,
      color: 'var(--accent-emerald)',
      animClass: 'anim-bell-ring'
    },
    {
      key: 'completed',
      labelEn: 'Served • Enjoy your meal',
      labelFi: 'Tarjoiltu • Hyvää ruokahalua',
      subEn: 'Thank you for dining with The Oven Story',
      subFi: 'Kiitos käynnistä The Oven Storyssa',
      icon: Utensils,
      color: 'var(--accent-emerald)'
    }
  ];

  const statusOrder: OrderStatus[] = ['submitted', 'confirmed', 'preparing', 'ready', 'completed'];
  const currentIndex = Math.max(0, statusOrder.indexOf(order.status));
  const currentStage = statusStages[currentIndex] || statusStages[0];
  const StageIcon = currentStage.icon;

  const handleCancel = async () => {
    if (confirm('Haluatko varmasti peruuttaa tilauksen? / Are you sure you want to cancel?')) {
      await cancelCustomerOrder(order.id);
    }
  };

  // Preview string of dishes
  const itemsPreview = order.items.map(it => `${it.quantity}x ${it.name}`).join(', ');

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      {/* Minimalist Live Tracking Card with Vector Illustrators */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-medium)',
          borderRadius: 'var(--radius-xl)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Top Header Row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              className="anim-radar-pulse"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: currentStage.color,
                display: 'inline-block'
              }}
            />
            <span style={{ fontWeight: 800, fontSize: '0.96rem', color: 'var(--text-primary)' }}>
              Pöytä #{order.table_number}
            </span>
            <span
              style={{
                background: 'var(--accent-copper-light)',
                color: 'var(--accent-copper)',
                padding: '2px 7px',
                borderRadius: 'var(--radius-xs)',
                fontWeight: 800,
                fontSize: '0.78rem'
              }}
            >
              {order.order_number}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              ({order.items.length} {lang === 'en' ? 'items' : 'tuotetta'})
            </span>
            <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--accent-copper)' }}>
              {formatEuro(order.total_amount)}
            </span>
          </div>
        </div>

        {/* Vector Illustrated Live In-Progress Action Banner */}
        <div
          style={{
            background: order.status === 'completed'
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)'
              : order.status === 'ready'
              ? 'rgba(16, 185, 129, 0.12)'
              : 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: '14px 16px',
            border: order.status === 'completed'
              ? '1.5px solid var(--accent-emerald)'
              : '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: order.status === 'completed' ? '0 4px 20px rgba(16, 185, 129, 0.25)' : undefined
          }}
        >
          {/* Vector Illustrator Icon Box */}
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${currentStage.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: currentStage.color,
              flexShrink: 0
            }}
          >
            <StageIcon size={22} className={currentStage.animClass} />
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '0.94rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{lang === 'en' ? currentStage.labelEn : currentStage.labelFi}</span>
              {order.status === 'completed' && <Sparkles size={16} color="#f59e0b" />}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              {lang === 'en' ? currentStage.subEn : currentStage.subFi}
            </div>
          </div>
        </div>

        {/* Dynamic Animated Segmented Progress Bar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', gap: '4px', height: '6px', borderRadius: '4px', overflow: 'hidden', background: 'var(--bg-surface)' }}>
            {[0, 1, 2, 3].map(stepIdx => {
              const isPast = currentIndex > stepIdx;
              const isCurrent = currentIndex === stepIdx;

              return (
                <div
                  key={stepIdx}
                  className={isCurrent ? 'active-progress-stream' : ''}
                  style={{
                    flex: 1,
                    background: isPast
                      ? 'var(--accent-emerald)'
                      : isCurrent
                      ? currentStage.color
                      : 'var(--border-subtle)',
                    transition: 'all 0.3s ease'
                  }}
                />
              );
            })}
          </div>

          {/* Clean Step Counter */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <span>Vaihe {Math.min(currentIndex + 1, 4)} / 4</span>
            <span style={{ color: currentStage.color, fontWeight: 700 }}>
              {order.status === 'preparing' ? 'Uunissa paistumassa' : order.status === 'ready' ? 'Valmis tarjoiltavaksi' : 'Valmistelussa'}
            </span>
          </div>
        </div>

        {/* Collapsible Order Items Section */}
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden'
          }}
        >
          {/* Summary / Toggle Bar */}
          <div
            onClick={() => setIsItemsExpanded(!isItemsExpanded)}
            style={{
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }}>
              <strong>{lang === 'en' ? 'Items:' : 'Tilatut:'}</strong> {itemsPreview}
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              {isItemsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>

          {/* Expanded Itemized List */}
          {isItemsExpanded && (
            <div
              style={{
                padding: '0 14px 12px',
                borderTop: '1px solid var(--border-subtle)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                paddingTop: '10px'
              }}
            >
              {order.items.map(it => (
                <div
                  key={it.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    fontSize: '0.82rem'
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 800, color: 'var(--accent-copper)' }}>{it.quantity}x </span>
                    <span>{it.name}</span>
                    {it.selected_variants && it.selected_variants.length > 0 && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '16px' }}>
                        {it.selected_variants.map(v => v.option_name).join(', ')}
                      </div>
                    )}
                  </div>
                  <span style={{ fontWeight: 700 }}>{formatEuro(it.price * it.quantity)}</span>
                </div>
              ))}

              {order.special_notes && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', paddingTop: '4px' }}>
                  "{order.special_notes}"
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {order.status === 'submitted' && (
            <button
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: 'var(--accent-rose)',
                borderRadius: 'var(--radius-md)',
                fontWeight: 700,
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <X size={14} />
              <span>{lang === 'en' ? 'Cancel Order' : 'Peruuta tilaus'}</span>
            </button>
          )}

          {(order.status === 'confirmed' || order.status === 'preparing' || order.status === 'ready') && (
            <div
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.78rem',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <ChefHat size={14} color="var(--accent-copper)" />
              <span>{lang === 'en' ? 'Order confirmed • In preparation (Locked)' : 'Tilaus vahvistettu • Valmistuksessa (Lukittu)'}</span>
            </div>
          )}

          {order.status === 'completed' && (
            <button
              className="btn-primary"
              onClick={() => setActiveCustomerOrder(null)}
              style={{ width: '100%', padding: '10px', fontSize: '0.88rem' }}
            >
              <Sparkles size={16} />
              <span>{lang === 'en' ? 'Order more items' : 'Tilaa lisää tuotteita'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
